import React, { useRef, useState, useEffect } from "react";
import app from "nystem";
import { sizeInSteps } from "../client/imageSize.js";
import ImageFallback from "./imageFallback.js";
import "./image.css";

const isResized = /(\/image\/.*)([0-9]{1,8})x([0-9]{1,8})\/(.*)/im;
const resizedParts = /(\/image.*\/)(([0-9]{1,8})x([0-9]{1,8})\/)(.*)/im;

const ResizedImage = ({ src, secure, cache, contentType, fieldId, fixed, className, limit, ...props }) => {
  const imageRef = useRef();
  const [, , , setWidth, setHeight, filename] = resizedParts.exec(src);

  const [style, setStyle] = useState({});
  const [imgSrc, setImgSrc] = useState("loading");
  const [retry, setRetry] = useState(0);
  const [loadingSize, setLoadingSize] = useState(`${setWidth}x${setHeight}`);
  const [currentSize, setCurrentSize] = useState("0x0");

  const onLoad = (event) => {
    setCurrentSize(`${event.currentTarget.naturalWidth}x${event.currentTarget.naturalHeight}`);
    props.onLoad?.(event);
  };

  const density = window.devicePixelRatio;

  className =
    className instanceof Array
      ? className
          .flat(Infinity)
          .filter((item) => item)
          .join(" ")
      : className;
  className = className || "";

  useEffect(() => {
    let request = 0;
    const loadImage = async () => {
      const current = ++request;
      let width;
      let height;

      const setSize = () => {
        const { offsetWidth, offsetHeight } = imageRef.current;

        if (fixed) {
          width = offsetWidth;
          height = offsetHeight;
        } else {
          const compWidth = (setWidth / setHeight) * offsetHeight;
          const compHeight = (setHeight / setWidth) * offsetWidth;
          width = compWidth < offsetWidth ? offsetWidth : compWidth;
          height = compHeight < offsetHeight ? offsetHeight : compHeight;
        }

        if (Number.isNaN(width)) width = 50;
        if (Number.isNaN(height)) height = 50;

        setStyle({ width, height });
      };

      if (!imageRef.current) return;
      setSize();
      if (width === 0 || height === 0) return;
      const imgWidth = sizeInSteps((fixed ? imageRef.current.getBoundingClientRect().width : width) * density, app.settings.imageSizeStep);
      const imgHeight = sizeInSteps((imgWidth * height) / width, app.settings.imageSizeStep);
      setLoadingSize(`${imgWidth}x${imgHeight}`);
      const original = filename.endsWith(".svg") || limit && imgWidth > limit;
      const { preview, ready, offline } = await app.event("imageRequest", {
        src, secure, cache, contentType, fieldId,
        width: original ? 0 : imgWidth, height: original ? 0 : imgHeight,
      });
      if (current !== request) return;
      if (offline) setImgSrc((imgSrc) => imgSrc === "loading" ? "offline" : imgSrc);
      let finished = false;
      if (preview) preview.then(async (result) => {
        if (result === "error") return;
        const { width, height } = await app.event("imageDimensions", { src: result });
        if (!finished && current === request) {
          setCurrentSize(`${width}x${height}`);
          setImgSrc(result);
        }
      });
      const result = await ready;
      finished = true;
      if (current === request) {
        if (result !== "error") setLoadingSize(undefined);
        setImgSrc((imgSrc) => result !== "error" ? result : ["loading", "offline"].includes(imgSrc) ? "error" : imgSrc);
      }
    };

    let resizeTimer = false;

    let width = window.innerWidth;
    const handleResizeDebounce = () => {
      if (width === window.innerWidth) return;
      width = window.innerWidth;

      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(handleResize, 200);
    };

    const handleResize = () => {
      setStyle({});
      loadImage();
    };

    window.addEventListener("resize", handleResizeDebounce);

    setImgSrc("loading");
    setCurrentSize("0x0");
    setLoadingSize(`${setWidth}x${setHeight}`);
    loadImage();
    return () => {
      request++;
      window.removeEventListener("resize", handleResizeDebounce, false);
      if (resizeTimer) clearTimeout(resizeTimer);
    };
  }, [cache, contentType, density, fieldId, filename, fixed, limit, retry, secure, setHeight, setWidth, src]);

  if (imgSrc === "error" || imgSrc === "offline")
    return (
      <ImageFallback
        className={`${className} nystem-image-placeholder`}
        imageRef={imageRef}
        imgSrc={imgSrc}
        setRetry={setRetry}
        style={{
          ...style,
          display: "grid",
        }}
      >
        {imgSrc === "offline"
          ? <OfflineIcon style={{ gridArea: "1 / 1", placeSelf: "center", width: "50%", maxWidth: "100px", opacity: 0.7 }} />
          : <ErrorIcon style={{ gridArea: "1 / 1", placeSelf: "center", width: "50%", maxWidth: "100px", opacity: 0.7 }} />}
        <LoadingSize>{`${currentSize} → ${loadingSize}`}</LoadingSize>
      </ImageFallback>
    );
  if (imgSrc === "loading")
    return (
      <div
        className={`${className} nystem-image-placeholder`}
        ref={imageRef}
        style={{
          ...style,
          display: "grid",
        }}
      >
        <span className="nystem-image-spinner col-start-1 row-start-1 place-self-center" role="status" aria-label="Loading image" />
        <LoadingSize>{`${currentSize} → ${loadingSize}`}</LoadingSize>
      </div>
    );

  if (loadingSize) return (
    <div className={`${className} grid-cols-1 grid-rows-1`} ref={imageRef} style={{ ...style, display: "grid" }}>
      <img {...props} onLoad={onLoad} alt={props.alt} src={imgSrc} className={`col-start-1 row-start-1 min-h-0 min-w-0 h-full w-full ${className.split(" ").filter((item) => /(^|:)object-/.test(item)).join(" ")}`} />
      <LoadingSize>{`${currentSize} → ${loadingSize}`}</LoadingSize>
    </div>
  );
  return <img className={className} ref={imageRef} alt={props.alt} {...props} onLoad={onLoad} src={imgSrc} style={style} />;
};

const FullSizeImage = ({ src, secure, cache, contentType, fieldId, className, ...props }) => {
  const imageRef = useRef();
  const [imgSrc, setImgSrc] = useState("loading");
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setImgSrc("loading");
    app.event("imageRequest", { src, secure, cache, contentType, fieldId }).then(({ ready, offline }) => {
      if (offline && active) setImgSrc("offline");
      return ready;
    }).then((result) => { if (active) setImgSrc(result); });
    return () => { active = false; };
  }, [src, secure, cache, contentType, fieldId, retry]);
  className = Array.isArray(className) ? className.flat(Infinity).filter(Boolean).join(" ") : className || "";
  if (["loading", "error", "offline"].includes(imgSrc)) return (
    <ImageFallback imageRef={imageRef} imgSrc={imgSrc} setRetry={setRetry} className={`${className} nystem-image-placeholder`} style={{ ...props.style, display: "grid" }}>
      {imgSrc === "offline" ? <OfflineIcon style={{ gridArea: "1 / 1", placeSelf: "center", width: "50%", maxWidth: "100px", opacity: 0.7 }} /> : imgSrc === "error" ? <ErrorIcon style={{ gridArea: "1 / 1", placeSelf: "center", width: "50%", maxWidth: "100px", opacity: 0.7 }} /> : <span className="nystem-image-spinner col-start-1 row-start-1 place-self-center" role="status" aria-label="Loading image" />}
      {props.width && props.height ? <LoadingSize>{`0x0 → ${props.width}x${props.height}`}</LoadingSize> : null}
    </ImageFallback>
  );
  return <img {...props} ref={imageRef} className={className} src={imgSrc} alt={props.alt} />;
};

const Image = (props) => isResized.test(props.src) ? <ResizedImage {...props} /> : <FullSizeImage {...props} />;

const LoadingSize = ({ children }) => (
  <span aria-hidden="true" className="pointer-events-none z-10 col-start-1 row-start-1 m-1 self-start justify-self-start text-[10px] font-normal leading-none text-white/50 [text-shadow:0_1px_2px_black]">
    {children}
  </span>
);

const OfflineIcon = ({ style }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={style} role="img" aria-label="Offline">
    <path d="M7 18h10.5a3.5 3.5 0 0 0 .7-6.93A6 6 0 0 0 7.1 9.1 4.5 4.5 0 0 0 7 18z" />
    <path d="M3 3l18 18" />
  </svg>
);

const ErrorIcon = ({ style }) => (
  <svg
    version="1.1"
    x="0px"
    y="0px"
    viewBox="0 0 1000 1000"
    enableBackground="new 0 0 1000 1000"
    xmlSpace="preserve"
    style={style || { width: "50%", height: "50%", opacity: "0.7", maxHeight: "100px", maxWidth: "100px" }}
  >
    <g>
      <path d="M847.5,154.7C758.4,65.8,635.4,10.5,500,10.5c-135.4,0-256.5,55.2-345.7,144.2C65.3,243.7,10,364.8,10,500c0,135.3,55.3,258.1,144.3,347.1c89.1,89,210.2,142.4,345.7,142.4c135.4,0,258.4-53.3,347.5-142.4C936.5,758.1,990,635.2,990,500C990,364.8,936.6,243.7,847.5,154.7z M84.9,500c0-113.9,48.1-217.2,122.9-291.9C282.6,133.4,386,85.3,500,85.3c101.7,0,193.8,35.7,265.4,96.3L181.2,765.2C120.6,693.5,84.9,601.6,84.9,500z M794,793.7c-74.8,74.7-178.1,121-294,121c-100.9,0-193.4-36.3-265.2-96.5l583.8-583.2c60.2,71.8,96.6,164.1,96.6,264.9C915.2,615.7,868.8,718.9,794,793.7z" />
    </g>
  </svg>
);
export default Image;
